import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Maths from "./Maths";
import { api, type Copie, type Grille, type Question } from "./api";
import { afficherNote, totauxCopie } from "./calculs";
import { fondNote, validation } from "./palette";

const DELAI_ENREGISTREMENT = 700; // ms d'inactivité avant écriture

type Position = { ligne: number; colonne: number };

/** Regroupe les questions par exercice, pour l'en-tête sur deux niveaux. */
function grouperParExercice(questions: Question[]) {
  const groupes: { numero: number; titre: string; nombre: number }[] = [];
  for (const question of questions) {
    const dernier = groupes.at(-1);
    if (dernier && dernier.numero === question.exercice) {
      dernier.nombre += 1;
    } else {
      groupes.push({
        numero: question.exercice,
        titre: question.titre_exercice,
        nombre: 1,
      });
    }
  }
  return groupes;
}

/** Exercices distincts, dans l'ordre d'apparition.
 *
 * `grouperParExercice` découpe en tranches contiguës pour les `colSpan` de
 * l'en-tête ; les sous-totaux veulent la liste sans doublon, même si un jour
 * un devoir revenait à un exercice déjà commencé.
 */
function exercicesDistincts(questions: Question[]) {
  const vus = new Map<number, string>();
  for (const question of questions) {
    if (!vus.has(question.exercice)) vus.set(question.exercice, question.titre_exercice);
  }
  return [...vus].map(([numero, titre]) => ({ numero, titre }));
}

/** Barème de chaque exercice, hors bonus — comme `totauxCopie.parExercice`. */
function baremeParExercice(questions: Question[]) {
  const totaux: Record<number, number> = {};
  for (const question of questions) {
    if (question.bonus) continue;
    totaux[question.exercice] = (totaux[question.exercice] ?? 0) + question.poids;
  }
  return totaux;
}

export default function GrilleSaisie({ dsId }: { dsId: number }) {
  const [grille, setGrille] = useState<Grille | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [position, setPosition] = useState<Position>({ ligne: 0, colonne: 0 });
  const [enregistrement, setEnregistrement] = useState<"repos" | "encours" | "echec">(
    "repos",
  );
  // Référence de la question dont le poids est en cours d'édition.
  const [poidsEdite, setPoidsEdite] = useState<string | null>(null);
  const [brouillonPoids, setBrouillonPoids] = useState("");
  // Validation déjà faite au clavier : le champ qui disparaît ne doit pas la
  // rejouer par son `blur`. Remis à faux par le `focus` du champ suivant, car
  // selon le navigateur ce `blur` a lieu ou non.
  const poidsDejaValide = useRef(false);

  // Notes modifiées mais pas encore écrites, par copie.
  const enAttente = useRef<Map<number, Record<string, number | null>>>(new Map());
  const minuteur = useRef<number | undefined>(undefined);
  const cellules = useRef<Map<string, HTMLInputElement>>(new Map());

  useEffect(() => {
    api.lireGrille(dsId).then(setGrille).catch((e: Error) => setErreur(e.message));
  }, [dsId]);

  const questions = grille?.questions ?? [];
  const copies = grille?.copies ?? [];
  const groupes = useMemo(() => grouperParExercice(questions), [questions]);
  const exercices = useMemo(() => exercicesDistincts(questions), [questions]);
  const baremeExercice = useMemo(() => baremeParExercice(questions), [questions]);
  // Un devoir d'un seul exercice — cinq des douze du corpus — y recopierait
  // le total. Même règle que les vues de découpage de `documents.py`.
  const detaille = exercices.length > 1;

  const ecrire = useCallback(async () => {
    const lots = [...enAttente.current.entries()];
    enAttente.current.clear();
    if (lots.length === 0) return;

    setEnregistrement("encours");
    try {
      await Promise.all(lots.map(([copieId, notes]) => api.enregistrerNotes(copieId, notes)));
      setEnregistrement("repos");
    } catch {
      // Les notes restent affichées : c'est l'écriture qui a échoué, pas la saisie.
      setEnregistrement("echec");
    }
  }, []);

  // Écriture différée : corriger une copie entière ne doit pas déclencher
  // 25 requêtes.
  const programmerEcriture = useCallback(() => {
    window.clearTimeout(minuteur.current);
    minuteur.current = window.setTimeout(ecrire, DELAI_ENREGISTREMENT);
  }, [ecrire]);

  // Ne jamais perdre une saisie parce que l'onglet se ferme.
  useEffect(() => {
    const avantFermeture = (evenement: BeforeUnloadEvent) => {
      if (enAttente.current.size > 0) evenement.preventDefault();
    };
    window.addEventListener("beforeunload", avantFermeture);
    return () => window.removeEventListener("beforeunload", avantFermeture);
  }, []);

  const saisir = useCallback(
    (ligne: number, question: Question, valeur: number | null) => {
      setGrille((precedent) => {
        if (!precedent) return precedent;
        const copies = [...precedent.copies];
        const copie = { ...copies[ligne], notes: { ...copies[ligne].notes } };
        if (valeur === null) {
          delete copie.notes[question.ref];
        } else {
          copie.notes[question.ref] = valeur;
        }
        copies[ligne] = copie;

        const lot = enAttente.current.get(copie.copie_id) ?? {};
        lot[question.ref] = valeur;
        enAttente.current.set(copie.copie_id, lot);

        return { ...precedent, copies };
      });
      programmerEcriture();
    },
    [programmerEcriture],
  );

  const deplacer = useCallback(
    (ligne: number, colonne: number) => {
      // Enchaînement naturel : on avance le long d'une copie, puis on passe
      // à l'élève suivant.
      let l = ligne;
      let c = colonne;
      if (c >= questions.length) {
        c = 0;
        l += 1;
      } else if (c < 0) {
        c = questions.length - 1;
        l -= 1;
      }
      if (l < 0 || l >= copies.length) return;

      setPosition({ ligne: l, colonne: c });
      cellules.current.get(`${l}:${c}`)?.focus();
    },
    [questions.length, copies.length],
  );

  const auClavier = (
    evenement: React.KeyboardEvent<HTMLInputElement>,
    ligne: number,
    colonne: number,
  ) => {
    const touche = evenement.key;

    if (/^[0-4]$/.test(touche)) {
      evenement.preventDefault();
      saisir(ligne, questions[colonne], Number(touche));
      deplacer(ligne, colonne + 1); // saisir fait avancer : c'est tout l'intérêt
      return;
    }

    const deplacements: Record<string, [number, number]> = {
      ArrowRight: [0, 1],
      ArrowLeft: [0, -1],
      ArrowDown: [1, 0],
      ArrowUp: [-1, 0],
      Enter: [0, 1],
      Tab: [0, evenement.shiftKey ? -1 : 1],
    };

    if (touche in deplacements) {
      evenement.preventDefault();
      const [dl, dc] = deplacements[touche];
      deplacer(ligne + dl, colonne + dc);
      return;
    }

    if (touche === "Backspace" || touche === "Delete") {
      evenement.preventDefault();
      saisir(ligne, questions[colonne], null);
      if (touche === "Backspace") deplacer(ligne, colonne - 1);
    }
  };

  const ouvrirPoids = (question: Question) => {
    setPoidsEdite(question.ref);
    setBrouillonPoids(String(question.poids).replace(".", ","));
  };

  /**
   * Enregistre le poids saisi, puis ouvre celui de `suivante` — même
   * enchaînement que la saisie des notes : on parcourt la ligne de barème sans
   * revenir à la souris. `suivante` vaut `null` en bout de ligne : l'édition se
   * ferme, il n'y a pas d'élève suivant où repartir.
   */
  const validerPoids = async (question: Question, suivante: Question | null = null) => {
    if (suivante) ouvrirPoids(suivante);
    else setPoidsEdite(null);
    // La virgule décimale est l'écriture naturelle en français.
    const valeur = Number(brouillonPoids.replace(",", "."));
    if (!Number.isFinite(valeur) || valeur < 0 || valeur === question.poids) return;

    setGrille((precedent) =>
      precedent
        ? {
            ...precedent,
            questions: precedent.questions.map((q) =>
              q.ref === question.ref ? { ...q, poids: valeur } : q,
            ),
          }
        : precedent,
    );

    try {
      await api.modifierBareme(dsId, { [question.ref]: valeur });
      setEnregistrement("repos");
    } catch {
      setEnregistrement("echec");
    }
  };

  const auClavierPoids = (
    evenement: React.KeyboardEvent<HTMLInputElement>,
    question: Question,
    colonne: number,
  ) => {
    const touche = evenement.key;
    if (touche === "Escape") {
      // Sans ce drapeau, le `blur` qui suit la fermeture enregistrerait le
      // brouillon : Échap n'annulerait rien.
      poidsDejaValide.current = true;
      setPoidsEdite(null);
      return;
    }
    // Les flèches gauche / droite déplacent le curseur dans le nombre : les
    // détourner rendrait « 1,5 » impossible à corriger.
    if (touche !== "Enter" && touche !== "Tab") return;

    evenement.preventDefault();
    poidsDejaValide.current = true;
    validerPoids(question, questions[colonne + (evenement.shiftKey ? -1 : 1)] ?? null);
  };

  // Le classeur est composé côté serveur, depuis la base : les saisies encore
  // en attente d'écriture n'y seraient pas. On vide la file d'abord.
  const exporter = async () => {
    window.clearTimeout(minuteur.current);
    await ecrire();
    const lien = document.createElement("a");
    lien.href = api.lienExportTableur(dsId);
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
  };

  const basculerStatut = async (ligne: number) => {
    const copie = copies[ligne];
    const suivant: Copie["statut"] = copie.statut === "presente" ? "absente" : "presente";
    await api.changerStatut(copie.copie_id, suivant);
    setGrille((precedent) => {
      if (!precedent) return precedent;
      const copies = [...precedent.copies];
      copies[ligne] = { ...copies[ligne], statut: suivant };
      return { ...precedent, copies };
    });
  };

  if (erreur) return <p className="erreur">Chargement impossible : {erreur}</p>;
  if (!grille) return <p>Chargement…</p>;

  const totalSaisi = copies.reduce((somme, c) => somme + Object.keys(c.notes).length, 0);
  const totalAttendu = copies.filter((c) => c.statut === "presente").length * questions.length;

  // Recalculé depuis les poids affichés : le total suit l'édition en direct.
  const totalBareme = questions
    .filter((q) => !q.bonus)
    .reduce((somme, q) => somme + q.poids, 0);
  const totalBonus = questions
    .filter((q) => q.bonus)
    .reduce((somme, q) => somme + q.poids, 0);
  const baremeJuste = Math.abs(totalBareme - 20) < 0.005;


  return (
    <section className="saisie">
      <header className="saisie-entete">
        <div>
          <h2>{grille.ds.titre}</h2>
          <p className="sous-titre">
            {grille.ds.classe} · {copies.length} élèves · {questions.length} questions ·{" "}
            <span
              className={baremeJuste ? "bareme-juste" : "bareme-decale"}
              style={{ color: baremeJuste ? validation.conforme : validation.ecart }}
            >
              barème sur {afficherNote(totalBareme)}
              {!baremeJuste && ` (écart de ${afficherNote(totalBareme - 20)})`}
            </span>
          </p>
        </div>
        <div className="avancement">
          <button type="button" className="export-tableur" onClick={exporter}
                  title="Le classeur reprend les poids et les notes en formules : modifier un poids y recalcule les notes.">
            Exporter en xlsx
          </button>
          <progress value={totalSaisi} max={totalAttendu || 1} />
          <span>
            {totalSaisi} / {totalAttendu} saisies
            {enregistrement === "encours" && " · enregistrement…"}
            {enregistrement === "echec" && (
              <strong className="erreur"> · échec d'enregistrement</strong>
            )}
          </span>
        </div>
      </header>

      <div className="question-courante">
        {questions[position.colonne] && (
          <>
            <span className="ref">{questions[position.colonne].ref}</span>
            <Maths texte={questions[position.colonne].label} />
          </>
        )}
      </div>

      <div className="grille-defilement">
        <table className="grille">
          <thead>
            <tr>
              <th className="colonne-eleve" rowSpan={3}>
                Élève
              </th>
              {groupes.map((groupe) => (
                <th key={groupe.numero} colSpan={groupe.nombre} className="entete-exercice">
                  Ex {groupe.numero} — {groupe.titre}
                </th>
              ))}
              {detaille && (
                <th
                  colSpan={exercices.length}
                  className="entete-exercice premier-sous-total"
                >
                  Points par exercice
                </th>
              )}
              <th rowSpan={2} className="colonne-total">
                Note
              </th>
            </tr>
            <tr>
              {questions.map((question) => (
                <th key={question.ref} title={question.label} className="entete-question">
                  {question.ref.replace(/^Ex\d+_/, "")}
                  {question.bonus && <span className="marque-bonus"> ★</span>}
                </th>
              ))}
              {detaille &&
                exercices.map((exercice, rang) => (
                  <th
                    key={exercice.numero}
                    className={`colonne-sous-total ${rang === 0 ? "premier-sous-total" : ""}`}
                    title={exercice.titre}
                  >
                    Ex {exercice.numero}
                  </th>
                ))}
            </tr>
            <tr>
              {questions.map((question, colonne) => (
                <th key={question.ref} className="entete-poids">
                  {poidsEdite === question.ref ? (
                    <input
                      autoFocus
                      value={brouillonPoids}
                      // Contenu présélectionné : taper remplace le poids au lieu
                      // d'obliger à l'effacer d'abord.
                      onFocus={(e) => {
                        poidsDejaValide.current = false;
                        e.currentTarget.select();
                      }}
                      onChange={(e) => setBrouillonPoids(e.target.value)}
                      onBlur={() => {
                        if (!poidsDejaValide.current) validerPoids(question);
                      }}
                      onKeyDown={(e) => auClavierPoids(e, question, colonne)}
                      className="saisie-poids"
                      inputMode="decimal"
                      aria-label={`Barème de ${question.ref}`}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => ouvrirPoids(question)}
                      title="Cliquer pour modifier le barème"
                    >
                      {afficherNote(question.poids)}
                    </button>
                  )}
                </th>
              ))}
              {detaille &&
                exercices.map((exercice, rang) => (
                  <th
                    key={exercice.numero}
                    className={`colonne-sous-total total-bareme ${
                      rang === 0 ? "premier-sous-total" : ""
                    }`}
                  >
                    {afficherNote(baremeExercice[exercice.numero] ?? 0)}
                  </th>
                ))}
              <th
                className={`colonne-total total-bareme ${
                  baremeJuste ? "bareme-juste" : "bareme-decale"
                }`}
                style={{ color: baremeJuste ? validation.conforme : validation.ecart }}
                title={
                  baremeJuste
                    ? "Le barème tombe sur 20"
                    : `Écart de ${afficherNote(totalBareme - 20)} point(s) par rapport à 20`
                }
              >
                {afficherNote(totalBareme)}
                {totalBonus > 0 && (
                  <span className="mention-bonus"> +{afficherNote(totalBonus)}</span>
                )}
              </th>
            </tr>
          </thead>

          <tbody>
            {copies.map((copie, ligne) => {
              const totaux = totauxCopie(copie, questions);
              const absente = copie.statut !== "presente";
              return (
                <tr
                  key={copie.copie_id}
                  className={[
                    absente ? "absente" : "",
                    ligne === position.ligne ? "ligne-active" : "",
                  ].join(" ")}
                >
                  <th className="colonne-eleve">
                    <button
                      type="button"
                      onClick={() => basculerStatut(ligne)}
                      title={absente ? "Marquer présent" : "Marquer absent"}
                    >
                      {copie.nom} {copie.prenom}
                    </button>
                  </th>

                  {questions.map((question, colonne) => {
                    const valeur = copie.notes[question.ref];
                    return (
                      <td key={question.ref} className={question.bonus ? "bonus" : ""}>
                        <input
                          ref={(element) => {
                            const cle = `${ligne}:${colonne}`;
                            if (element) cellules.current.set(cle, element);
                            else cellules.current.delete(cle);
                          }}
                          value={valeur ?? ""}
                          readOnly
                          disabled={absente}
                          inputMode="numeric"
                          className={valeur === undefined ? "vide" : "note"}
                          style={{ background: absente ? undefined : fondNote(valeur) }}
                          onFocus={() => setPosition({ ligne, colonne })}
                          onKeyDown={(e) => auClavier(e, ligne, colonne)}
                          aria-label={`${copie.nom} — ${question.ref}`}
                        />
                      </td>
                    );
                  })}

                  {detaille &&
                    exercices.map((exercice, rang) => {
                      // Cellule vide = 0 au calcul. Un exercice qu'on n'a pas
                      // encore atteint afficherait donc 0, qui se lit comme
                      // une note. Tant qu'aucune de ses questions n'est
                      // saisie, on écrit « — » : pas encore corrigé.
                      const entamee = questions.some(
                        (q) =>
                          q.exercice === exercice.numero &&
                          !q.bonus &&
                          copie.notes[q.ref] !== undefined,
                      );
                      return (
                        <td
                          key={exercice.numero}
                          className={`colonne-sous-total ${
                            rang === 0 ? "premier-sous-total" : ""
                          }`}
                        >
                          {absente || !entamee
                            ? "—"
                            : afficherNote(totaux.parExercice[exercice.numero] ?? 0)}
                        </td>
                      );
                    })}

                  <td className="colonne-total">
                    {absente ? "—" : afficherNote(totaux.noteFinale)}
                    {totaux.noteBonus > 0 && (
                      <span className="mention-bonus">
                        {" "}
                        (+{afficherNote(totaux.noteBonus)})
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="aide">
        Taper <kbd>0</kbd> à <kbd>4</kbd> saisit et avance. <kbd>←</kbd> <kbd>→</kbd>{" "}
        <kbd>↑</kbd> <kbd>↓</kbd> pour se déplacer, <kbd>Retour arrière</kbd> pour effacer.
        En fin de ligne, la saisie passe à l'élève suivant. Cliquer sur un nom bascule
        présent / absent. Cliquer sur un poids modifie le barème : <kbd>Entrée</kbd> ou{" "}
        <kbd>Tab</kbd> valide et ouvre le poids suivant, <kbd>Maj</kbd>+<kbd>Tab</kbd> le
        précédent, <kbd>Échap</kbd> annule.
      </p>
    </section>
  );
}
